<script setup>
import { computed } from 'vue';
import { Info, CircleCheck, TriangleAlert, OctagonAlert, ShieldCheck, Sparkles, X } from 'lucide-vue-next';

const props = defineProps({
  /** info, success, warning, danger, brand, plain */
  tone: { type: String, default: 'info' },
  title: { type: String, default: '' },
  /** Small caps tag on the right edge, for example "No live KYC" */
  tag: { type: String, default: '' },
  icon: { type: [Object, Function], default: null },
  dismissible: { type: Boolean, default: false },
});
defineEmits(['dismiss']);
const defaults = { info: Info, success: CircleCheck, warning: TriangleAlert, danger: OctagonAlert, brand: ShieldCheck, plain: Sparkles };
const ico = computed(() => props.icon || defaults[props.tone]);
</script>

<template>
  <div class="pd-notice" :class="`pd-notice--${tone}`" :role="tone === 'danger' ? 'alert' : 'status'">
    <component :is="ico" class="pd-notice__icon" :size="22" :stroke-width="2" aria-hidden="true" />
    <div class="pd-notice__body">
      <p v-if="title" class="pd-notice__title">{{ title }}</p>
      <div class="pd-notice__text"><slot /></div>
    </div>
    <span v-if="tag" class="pd-notice__tag">{{ tag }}</span>
    <button v-if="dismissible" type="button" class="pd-notice__x" aria-label="Dismiss" @click="$emit('dismiss')">
      <X :size="18" aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped>
.pd-notice {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 22px;
  border-radius: var(--pd-r-md);
  border: 1px solid transparent;
}
.pd-notice__body { flex: 1; min-width: 0; }
.pd-notice__title { font-weight: 800; margin-bottom: 2px; }
.pd-notice__text { font-size: var(--pd-fs-small); color: var(--pd-muted); }
.pd-notice__tag { align-self: center; font-size: var(--pd-fs-eyebrow); font-weight: 800; letter-spacing: var(--pd-track-wide); text-transform: uppercase; color: var(--pd-orange-text); }
.pd-notice__x { border: 0; background: none; padding: 2px; cursor: pointer; color: var(--pd-muted); }
.pd-notice--info { background: var(--pd-info-bg); }
.pd-notice--info .pd-notice__icon { color: var(--pd-info); }
.pd-notice--success { background: var(--pd-success-bg); }
.pd-notice--success .pd-notice__icon { color: var(--pd-success); }
.pd-notice--warning { background: var(--pd-warning-bg); }
.pd-notice--warning .pd-notice__icon { color: var(--pd-warning); }
.pd-notice--danger { background: var(--pd-danger-bg); }
.pd-notice--danger .pd-notice__icon { color: var(--pd-danger); }
.pd-notice--brand { background: var(--pd-tint); border-color: var(--pd-peach); }
.pd-notice--brand .pd-notice__icon { color: var(--pd-orange-text); }
.pd-notice--plain { background: transparent; padding: 0; }
.pd-notice--plain .pd-notice__icon { color: var(--pd-orange); width: 18px; }
@media (max-width: 560px) {
  .pd-notice { flex-wrap: wrap; }
  .pd-notice__tag { order: -1; flex-basis: 100%; }
}
</style>
